// Parses a paragraph's plain text into a card block if it matches one of our
// inline syntaxes. Priority: MCQ > basic > cloze (MCQ strictly extends basic).
//
// - MCQ:   "Question? -> Correct [distractor1, distractor2, ...]"
//          Brackets hold distractors; the correct answer appears before them.
//          Final options array = [correct, ...distractors], correct = [0].
// - Basic: "Question? -> Answer"
// - Cloze: any paragraph containing at least one {{cN::...}} marker.

export type ParsedCard =
  | { cardType: 'basic'; front: string; back: string }
  | { cardType: 'cloze'; front: string }
  | { cardType: 'mcq'; front: string; back: string; options: string[]; correct: number[] };

const ARROW = /\s*(?:->|→)\s*/;

export function parseCardSyntax(raw: string): ParsedCard | null {
  const text = raw.trim();
  if (!text) return null;

  const mcq = matchMcq(text);
  if (mcq) return mcq;

  const basic = matchBasic(text);
  if (basic) return basic;

  if (/\{\{c\d+::[^}]+\}\}/.test(text)) return { cardType: 'cloze', front: text };

  return null;
}

function matchMcq(text: string): ParsedCard | null {
  // Trailing [ ... ] group with comma-separated items.
  const bracket = text.match(/^(.+?)\s*\[([^\]]+)\]\s*$/);
  if (!bracket) return null;
  const beforeBrackets = bracket[1]!.trim();
  const distractors = bracket[2]!
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (distractors.length === 0) return null;

  const parts = beforeBrackets.split(ARROW);
  if (parts.length < 2) return null;
  const front = parts[0]!.trim();
  const correct = parts.slice(1).join(' -> ').trim();
  if (!front || !correct) return null;

  return {
    cardType: 'mcq',
    front,
    back: correct,
    options: [correct, ...distractors],
    correct: [0],
  };
}

function matchBasic(text: string): ParsedCard | null {
  if (!ARROW.test(text)) return null;
  const parts = text.split(ARROW);
  if (parts.length < 2) return null;
  const front = parts[0]!.trim();
  const back = parts.slice(1).join(' -> ').trim();
  if (!front || !back) return null;
  // Must not have a trailing bracket group — that's the MCQ shape.
  if (/\[[^\]]+\]\s*$/.test(back)) return null;
  return { cardType: 'basic', front, back };
}
